import { ParkingReservation } from '../types';

export type FilterType = 
    | 'All' 
    | 'Reserved' 
    | 'Checked In' 
    | 'Today Remaining' 
    | 'Due Check-Out'
    | 'Overdue Checkout';

// Helper to normalize status strings safely
export const normalizeStatus = (status?: string): string => {
    if (!status) return '';
    return status.toLowerCase().trim().replace(/[-_]/g, ' ');
};

// Helper to get today's date string in YYYY-MM-DD format safely
export const getTodayString = (): string => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

export const isReservationInactive = (res: ParkingReservation) => {
    const status = normalizeStatus(res.status);
    if (status === 'checkout' || status === 'disactivated') return true;
    
    // If they are parked/active, they are not inactive
    if (isCheckedInStatus(res.status)) return false;

    // For non-parked reservations, if they are very old (e.g. no-shows), they are inactive
    if (!res.endDate) return false;
    
    const endDate = new Date(res.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    const diffTime = today.getTime() - endDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 7;
};

// Status Category Helpers
export const isReservedStatus = (status: string) => {
    const s = normalizeStatus(status);
    return s === 'reservation' || s === 'reserved' || s === 'no show';
};

export const isCheckedInStatus = (status: string) => {
    const s = normalizeStatus(status);
    return s === 'check in' || 
           s === 'checked in' || 
           s === 'late departure' || 
           s === 'late checkout' || 
           s === 'overdue' || 
           s === 'due checkout';
};

export const isCheckedOutStatus = (status: string) => {
    const s = normalizeStatus(status);
    return s === 'check out' || s === 'checkout';
};

export const getSystemStatus = (res: ParkingReservation): string | null => {
    const status = normalizeStatus(res.status);
    const today = getTodayString();
    
    if (status === 'reservation' || status === 'reserved') {
        const arrival = res.startDate || res.hotelCheckInDate;
        if (arrival && arrival < today) return 'NO SHOW';
    }
    
    if (isCheckedInStatus(res.status)) {
        const departure = res.endDate || res.hotelCheckOutDate;
        if (departure) {
            if (departure < today) return 'OVERDUE';
            if (departure === today) return 'DUE TODAY';
        }
    }
    
    return null;
};

// Logical Condition Helpers
export const isReserved = (res: ParkingReservation) => {
    return isReservedStatus(res.status);
};

export const isCheckedIn = (res: ParkingReservation) => {
    return isCheckedInStatus(res.status);
};

export const isDueToday = (res: ParkingReservation) => {
    if (!isCheckedIn(res)) return false;
    const todayStr = getTodayString();
    const checkOutDate = res.endDate || res.hotelCheckOutDate;
    return checkOutDate === todayStr;
};

export const isOverdue = (res: ParkingReservation) => {
    if (!isCheckedIn(res)) return false;
    const todayStr = getTodayString();
    const checkOutDate = res.endDate || res.hotelCheckOutDate;
    return !!(checkOutDate && checkOutDate < todayStr);
};

export const isFutureDeparture = (res: ParkingReservation) => {
    if (!isCheckedIn(res)) return false;
    const todayStr = getTodayString();
    const checkOutDate = res.endDate || res.hotelCheckOutDate;
    return !!(checkOutDate && checkOutDate > todayStr);
};

// 1. Filter by Status
export const filterByStatus = (data: ParkingReservation[], status: string): ParkingReservation[] => {
    const normalizedTarget = normalizeStatus(status);
    return data.filter(res => {
        if (normalizedTarget === 'reserved') return isReserved(res);
        if (normalizedTarget === 'checked in') return isCheckedIn(res);
        return normalizeStatus(res.status) === normalizedTarget;
    });
};

// 2. Today's Remaining (Departure Date is AFTER today)
export const getTodayRemaining = (data: ParkingReservation[]): ParkingReservation[] => {
    return data.filter(res => isFutureDeparture(res));
};

// 3. Due Check-Out (Departure Date = Today)
export const getDueCheckouts = (data: ParkingReservation[]): ParkingReservation[] => {
    return data.filter(res => isDueToday(res));
};

// 4. Overdue Checkout (Departure Date is BEFORE today)
export const getOverdueCheckouts = (data: ParkingReservation[]): ParkingReservation[] => {
    return data.filter(res => isOverdue(res));
};

// 5. Calculate Counts
export const calculateCounts = (data: ParkingReservation[]) => {
    return {
        'All': data.length,
        'Reserved': data.filter(isReserved).length,
        'Checked In': data.filter(isCheckedIn).length,
        'Today Remaining': data.filter(isFutureDeparture).length,
        'Due Check-Out': data.filter(isDueToday).length,
        'Overdue Checkout': data.filter(isOverdue).length,
    };
};

// Main filtering orchestrator
export const applyFilters = (
    data: ParkingReservation[], 
    activeFilter: FilterType, 
    searchTerm: string
): ParkingReservation[] => {
    let filtered = data;

    // Apply specific filter logic
    switch (activeFilter) {
        case 'Reserved':
            filtered = data.filter(isReserved);
            break;
        case 'Checked In':
            filtered = data.filter(isCheckedIn);
            break;
        case 'Today Remaining':
            filtered = data.filter(isFutureDeparture);
            break;
        case 'Due Check-Out':
            filtered = data.filter(isDueToday);
            break;
        case 'Overdue Checkout':
            filtered = data.filter(isOverdue);
            break;
        case 'All':
        default:
            break;
    }

    // Apply search term if present
    if (searchTerm) {
        const searchLower = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(res => (
            (res.reservationSource || '').toLowerCase().includes(searchLower) ||
            (res.confirmationNumber || '').toLowerCase().includes(searchLower) ||
            (res.authorizedBy || '').toLowerCase().includes(searchLower) ||
            (res.vehicle_plate || '').toLowerCase().includes(searchLower) ||
            (res.guestName || '').toLowerCase().includes(searchLower) ||
            (res.roomNumber || '').toLowerCase().includes(searchLower)
        ));
    }

    return filtered;
};
